import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import FileAttachments from '../components/FileAttachments';
import { Building2, Plus, XCircle, CheckCircle2, Search, Edit, ChevronDown, ChevronUp, Paperclip, Send, RotateCcw, Trash2, AlertCircle, Pencil } from 'lucide-react';
import { BadgeEstadoFirma, ModalPasarPdteFirma, ModalRevertirBorrador, esEstadoFirmado, IconoBloqueado } from '../components/FlujoFirmaAcciones';
import { ModalEliminarRegistro, esAdmin } from '../components/RegistroAcciones';

// ═══════════════════════════════════════════════════════════════
// MODAL: Crear/Editar proveedor (usa contraseña si pendiente_firma)
// ═══════════════════════════════════════════════════════════════
function ModalProveedor({ proveedor, onClose, onGuardado }) {
  const [form, setForm] = useState(proveedor || {
    nombre: '', cif: '', contacto: '', email: '', telefono: '',
    direccion: '', tipo_producto: '', licencia_sanitaria: '',
    fecha_validez_licencia: '', estado: 'pendiente', observaciones: ''
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(proveedor?.id || null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  
  const necesitaPassword = proveedor?.estado_cualificacion === 'pendiente_firma';

  const guardar = async () => {
    if (!form.nombre) { setError('El nombre es obligatorio'); return; }
    if (necesitaPassword && !password) { setError('Debes introducir tu contraseña para editar (está en Pdte. firma)'); return; }
    setLoading(true);
    try {
      if (form.id) {
        const payload = necesitaPassword ? { ...form, password } : form;
        await api.put(`/proveedores/${form.id}`, payload);
        onGuardado();
      } else {
        const res = await api.post('/proveedores', form);
        setSavedId(res.data.id);
        setForm(f => ({ ...f, id: res.data.id }));
      }
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={18} color="var(--color-primary)" /> {proveedor ? 'Editar' : 'Nuevo'} Proveedor
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {savedId && !proveedor && (
            <div className="alert alert-success">
              <CheckCircle2 size={16} /> Proveedor guardado en Borrador. Adjunta licencia, contrato y documentación necesaria. Cuando esté completo, márcalo como "Pdte. firma" desde el listado.
            </div>
          )}
          {necesitaPassword && (
            <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
              <AlertCircle size={16} /> Este proveedor está pendiente de cualificación. Editar requiere contraseña de administrador.
            </div>
          )}

          <div className="form-grid">
            <div className="form-group"><label className="form-label">Nombre / Razón social <span className="required">*</span></label>
              <input className="form-control" value={form.nombre} onChange={e => set('nombre', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">CIF / NIF</label>
              <input className="form-control" style={{ fontFamily: 'var(--font-mono)' }} value={form.cif} onChange={e => set('cif', e.target.value)} /></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Persona de contacto</label>
              <input className="form-control" value={form.contacto} onChange={e => set('contacto', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Email</label>
              <input type="email" className="form-control" value={form.email} onChange={e => set('email', e.target.value)} /></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Teléfono</label>
              <input className="form-control" value={form.telefono} onChange={e => set('telefono', e.target.value)} /></div>
            <div className="form-group">
              <label className="form-label">Situación comercial</label>
              <select className="form-control" value={form.estado} onChange={e => set('estado', e.target.value)}>
                <option value="pendiente">Pendiente evaluación</option>
                <option value="activo">✅ Activo</option>
                <option value="inactivo">⚠ Inactivo</option>
                <option value="suspendido">⛔ Suspendido</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Dirección</label>
            <input className="form-control" value={form.direccion} onChange={e => set('direccion', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Tipo de producto / servicio</label>
            <input className="form-control" value={form.tipo_producto} onChange={e => set('tipo_producto', e.target.value)} placeholder="Ej: Productos sanitarios, material de laboratorio..." /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Nº Licencia sanitaria</label>
              <input className="form-control" style={{ fontFamily: 'var(--font-mono)' }} value={form.licencia_sanitaria} onChange={e => set('licencia_sanitaria', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Fecha validez licencia</label>
              <input type="date" className="form-control" value={form.fecha_validez_licencia} onChange={e => set('fecha_validez_licencia', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Observaciones</label>
            <textarea className="form-control" rows={3} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} /></div>

          {savedId && (
            <FileAttachments seccion="proveedores" entidadTipo="proveedor" entidadId={savedId}
              label="Adjuntar licencia sanitaria / contrato / documentación de cualificación" />
          )}
          {!savedId && (
            <div style={{ padding: '10px 12px', background: 'var(--color-gray-1)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--color-text-3)', marginTop: 16 }}>
              💡 Guarda el proveedor para poder adjuntar documentos
            </div>
          )}

          {necesitaPassword && (
            <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
              <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>🔐 Contraseña de administrador</label>
              <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>{savedId && !proveedor ? 'Cerrar' : 'Cancelar'}</button>
          {(!savedId || proveedor) && (
            <button className="btn btn-primary" onClick={guardar} disabled={loading}>
              {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
              {proveedor ? 'Guardar cambios' : 'Crear proveedor'}
            </button>
          )}
          {savedId && !proveedor && (
            <button className="btn btn-primary" onClick={onGuardado}><CheckCircle2 size={16} /> Finalizar</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILA
// ═══════════════════════════════════════════════════════════════
function FilaProveedor({ p, isAdmin, onEdit, onPasarPdte, onRevertir, onEliminar }) {
  const [expanded, setExpanded] = useState(false);
  const situacionCls = { activo: 'badge-firmado', pendiente: 'badge-pendiente_firma', inactivo: 'badge-archivado', suspendido: 'badge-rechazado' };
  
  const estadoCual = p.estado_cualificacion || 'borrador';
  const bloqueado = esEstadoFirmado(estadoCual, true);

  return (
    <>
      <tr>
        <td style={{ fontWeight: 600 }}>{p.nombre}</td>
        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>{p.cif || '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{p.contacto || '—'}</td>
        <td style={{ fontSize: '0.82rem', color: 'var(--color-text-2)' }}>{p.tipo_producto || '—'}</td>
        <td style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)' }}>{p.licencia_sanitaria || '—'}</td>
        <td><span className={`badge ${situacionCls[p.estado]}`}>{p.estado || 'pendiente'}</span></td>
        <td><BadgeEstadoFirma estado={estadoCual} esProveedor={true} /></td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileAttachments seccion="proveedores" entidadTipo="proveedor" entidadId={p.id} compact />
            
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setExpanded(!expanded)} title="Ver documentos">
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            
            {/* Editar — siempre visible, modal pedirá contraseña si está en pdte. firma */}
            {!bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEdit(p)} 
                title="Editar" style={{ color: '#3b82f6' }}>
                <Edit size={14} />
              </button>
            )}
            
            {/* Pasar a Pdte. cualificación — solo si está en borrador */}
            {['borrador', 'pendiente_cualificacion', null].includes(estadoCual) && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onPasarPdte(p)} 
                title="Solicitar cualificación" style={{ color: '#f59e0b' }}>
                <Send size={13} />
              </button>
            )}
            
            {/* Revertir — solo admin si está en pendiente_firma */}
            {estadoCual === 'pendiente_firma' && isAdmin && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onRevertir(p)} 
                title="Revertir a Borrador (admin)" style={{ color: '#6b7280' }}>
                <RotateCcw size={13} />
              </button>
            )}
            
            {/* Eliminar — admin, bloqueado si firmado */}
            {isAdmin && !bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEliminar(p)} 
                title="Eliminar (admin)" style={{ color: 'var(--color-danger)' }}>
                <Trash2 size={13} />
              </button>
            )}
            
            {bloqueado && <IconoBloqueado estado={estadoCual} />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} style={{ padding: '0 14px 14px', background: 'var(--color-gray-1)' }}>
            <FileAttachments seccion="proveedores" entidadTipo="proveedor" entidadId={p.id}
              label="Subir licencia sanitaria, contrato u otro documento" />
          </td>
        </tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA
// ═══════════════════════════════════════════════════════════════
export default function ProveedoresPage() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [pasarPdteRegistro, setPasarPdteRegistro] = useState(null);
  const [revertirRegistro, setRevertirRegistro] = useState(null);
  const [eliminarRegistro, setEliminarRegistro] = useState(null);
  const [success, setSuccess] = useState('');
  const isAdmin = esAdmin();

  const load = () => {
    setLoading(true);
    api.get('/proveedores', { params: { q, estado: filtroEstado } }).then(r => setProveedores(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [q, filtroEstado]);

  const mostrarSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Building2 size={24} color="var(--color-primary)" />Gestión de Proveedores</h1>
          <p>{proveedores.length} proveedores · Cada cualificación se firma por la Directora Técnica (AEMPS)</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({})}><Plus size={16} /> Nuevo Proveedor</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="search-wrap">
            <Search size={15} />
            <input className="search-input" placeholder="Buscar proveedor..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <select className="form-control" style={{ width: 200 }} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
            <option value="">Todas las situaciones</option>
            <option value="activo">Activo</option><option value="pendiente">Pendiente</option>
            <option value="inactivo">Inactivo</option><option value="suspendido">Suspendido</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          proveedores.length === 0 ? (
            <div className="empty-state"><Building2 size={48} /><h3>Sin proveedores</h3></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Nombre</th><th>CIF</th><th>Contacto</th>
                    <th>Tipo producto</th><th>Licencia</th>
                    <th>Situación</th><th>Cualificación</th>
                    <th><Paperclip size={13} /></th>
                  </tr>
                </thead>
                <tbody>
                  {proveedores.map(p => 
                    <FilaProveedor key={p.id} p={p} isAdmin={isAdmin}
                      onEdit={setModal}
                      onPasarPdte={setPasarPdteRegistro}
                      onRevertir={setRevertirRegistro}
                      onEliminar={setEliminarRegistro} />
                  )}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {modal !== null && (
        <ModalProveedor
          proveedor={modal?.id ? modal : null}
          onClose={() => setModal(null)}
          onGuardado={() => { setModal(null); load(); }}
        />
      )}
      
      {pasarPdteRegistro && <ModalPasarPdteFirma 
        endpoint={`/proveedores/${pasarPdteRegistro.id}/pasar-pdte-firma`}
        descripcion={`${pasarPdteRegistro.nombre} · ${pasarPdteRegistro.cif || ''}`}
        esProveedor={true}
        onClose={() => setPasarPdteRegistro(null)}
        onConfirmado={() => { setPasarPdteRegistro(null); load(); mostrarSuccess('Marcado como pendiente de cualificación. Ve a Firmas Pendientes.'); }} />}
      
      {revertirRegistro && <ModalRevertirBorrador 
        endpoint={`/proveedores/${revertirRegistro.id}/reset-estado`}
        descripcion={`${revertirRegistro.nombre} · ${revertirRegistro.cif || ''}`}
        onClose={() => setRevertirRegistro(null)}
        onConfirmado={() => { setRevertirRegistro(null); load(); mostrarSuccess('Revertido a borrador'); }} />}
      
      {eliminarRegistro && <ModalEliminarRegistro 
        endpoint={`/proveedores/${eliminarRegistro.id}`}
        descripcion={`${eliminarRegistro.nombre} · ${eliminarRegistro.cif || ''}`}
        onClose={() => setEliminarRegistro(null)}
        onEliminado={() => { setEliminarRegistro(null); load(); mostrarSuccess('Proveedor eliminado'); }} />}
    </div>
  );
}
